import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './firebase';

/**
 * Get a daily wellness message based on wellness score using Gemini AI via Cloud Function.
 * The API key is securely stored server-side.
 */
export const getDailyWellnessMessage = async (wellnessScore: number): Promise<string> => {
  try {
    const getMessageFn = httpsCallable(functions, 'enough_get_daily_wellness_message');
    const result = await getMessageFn({
      wellnessScore: wellnessScore
    });

    const data = result.data as { message?: string };
    return data.message || "Every step forward is a victory.";
  } catch (error) {
    console.error('Error getting wellness message:', error);
    return "Breathe. You are taking control.";
  }
};

/**
 * Get a celebratory message when all focus items are completed.
 * Cached per UTC day in Firestore.
 */
export const getFocusCelebrationMessage = async (completedCount: number): Promise<string> => {
  try {
    const getMessageFn = httpsCallable(functions, 'enough_get_focus_celebration_message');
    const result = await getMessageFn({
      completedCount: completedCount
    });

    const data = result.data as { message?: string };
    return data.message || "You showed up for yourself today.";
  } catch (error) {
    console.error('Error getting celebration message:', error);
    return "You showed up for yourself today.";
  }
};

export interface StreamCallbacks {
  onToken: (text: string) => void;
  onMetadata: (data: {
    remainingMessages?: number;
    sessionId?: string;
    privacyLexicon?: Record<string, string>;
  }) => void;
  onDone: () => void;
  onError: (error: string) => void;
}

/**
 * Stream a chat exchange from the Cloud Run chat service (services/chat/).
 * This is the single chat backend — the legacy Cloud Function fallback
 * (enough_chat_with_coach) was removed in 2026-07.
 */
export async function streamChatWithCoach(
  history: { role: string; text: string }[],
  message: string,
  callbacks: StreamCallbacks,
  signal?: AbortSignal,
  sessionId?: string,
): Promise<void> {
  const serviceUrl = import.meta.env.VITE_CHAT_SERVICE_URL;
  if (!serviceUrl) {
    throw new Error('VITE_CHAT_SERVICE_URL not configured');
  }

  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const token = await user.getIdToken();

  const response = await fetch(`${serviceUrl}/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ history, message, sessionId }),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    throw new Error(`Chat service error ${response.status}: ${errorText}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  // Scoped outside the read loop on purpose: an SSE frame is `event: X\ndata: {...}`,
  // and network chunks are not aligned to frames. If a chunk ends between those two
  // lines (most likely on the fat `metadata` frame, which carries the whole privacy
  // lexicon), resetting the type per chunk would drop the frame on the floor — losing
  // the sessionId and forcing the next send to open a brand new chat session.
  let eventType = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Parse SSE events from buffer
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          eventType = line.slice(7);
        } else if (line.startsWith('data: ')) {
          const data = line.slice(6);
          try {
            const parsed = JSON.parse(data);
            switch (eventType) {
              case 'token':
                callbacks.onToken(parsed.text);
                break;
              case 'metadata':
                callbacks.onMetadata(parsed);
                break;
              case 'done':
                callbacks.onDone();
                break;
              case 'error':
                callbacks.onError(parsed.error || 'Unknown error');
                break;
            }
          } catch {
            // Skip malformed JSON
          }
          eventType = '';
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
